<template>
  <div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-md-3 mb-2">
                <b-form-input
                  v-model="filter"
                  type="search"
                  :placeholder="$t('label.search')"
                  :aria-label="$t('label.search')"
                  maxlength="255"
                  @change="searchListing"
                  @keyup.enter="searchListing"
                  autocomplete="off"
                />
              </div>
              <div class="col-md-2 mb-2">
                <b-form-select
                  v-model="type"
                  :options="typeDdl"
                  :aria-label="$t('label.type')"
                  @change="searchListing"
                />
              </div>
              <div class="col-md-2 mb-2">
                <b-form-select
                  v-model="platformType"
                  :options="platformDdl"
                  aria-label="Platform"
                  @change="searchListing"
                />
              </div>
              <div class="col-md-3 mb-2">
                <b-form-select
                  v-model="reportStatus"
                  :options="statusDdl"
                  :aria-label="$t('label.status')"
                  @change="searchListing"
                />
              </div>
              <div class="col-md-2 mb-2">
                <b-button
                  class="btn-info col-12"
                  :disabled="!status.save"
                  @click="openForm()"
                  >{{ $t("label.createX", []) }}</b-button
                >
              </div>
            </div>
            <b-alert :show="!!errors.list" variant="danger">
              {{ errors.list }}
              <b-button
                size="sm"
                variant="outline-danger"
                @click="getReportList(searchCriteria)"
                >{{ $t("label.reset") }}</b-button
              >
            </b-alert>
            <div class="table-responsive mb-0">
              <b-table
                v-if="status.list && !errors.list"
                :items="tableRows"
                :fields="fields"
                :per-page="perPage"
                :sort-by="searchCriteria.sortBy"
                :sort-desc="searchCriteria.orderBy === 'desc'"
                no-local-sorting
                responsive="sm"
                @sort-changed="sortListing"
              >
                <template #cell(user)="data">{{
                  data.item.user
                    ? data.item.user.fullName ||
                      [data.item.user.firstName, data.item.user.lastName]
                        .filter(Boolean)
                        .join(" ") ||
                      data.item.user.email
                    : "—"
                }}</template>
                <template #cell(type)="data">{{
                  optionLabel(typeDdl, data.item.type)
                }}</template>
                <template #cell(platform_type)="data">{{
                  optionLabel(platformDdl, data.item.platformType)
                }}</template>
                <template #cell(description)="data"
                  ><div class="report-description">
                    {{ data.item.description }}
                  </div></template
                >
                <template #cell(status)="data">
                  <b-badge :variant="statusVariant(data.item.status)">{{
                    optionLabel(statusDdl, data.item.status)
                  }}</b-badge>
                </template>
                <template #cell(created_at)="data">{{
                  formatDate(data.item.createdAt)
                }}</template>
                <template #cell(actions)="data">
                  <b-button
                    variant="link"
                    class="text-info p-0"
                    :disabled="!status.save"
                    v-b-tooltip.hover
                    :title="
                      $t(
                        canEditReport(data.item)
                          ? 'label.updateX'
                          : 'label.viewX',
                        [$t('module.report')],
                      )
                    "
                    :aria-label="
                      $t(
                        canEditReport(data.item)
                          ? 'label.updateX'
                          : 'label.viewX',
                        [$t('module.report')],
                      )
                    "
                    @click="openForm(data.item)"
                  >
                    <i
                      class="mdi font-size-18"
                      :class="
                        canEditReport(data.item)
                          ? 'mdi-pencil'
                          : 'mdi-eye-outline'
                      "
                    ></i>
                  </b-button>
                  <b-button
                    v-if="canUpdateStatus"
                    variant="link"
                    class="text-info p-0 ml-2"
                    :disabled="!status.save"
                    v-b-tooltip.hover
                    :title="statusAction(data.item)"
                    :aria-label="statusAction(data.item)"
                    @click="statusConfirmation(data.item)"
                  >
                    <i
                      class="mdi mdi-swap-horizontal font-size-18"
                      aria-hidden="true"
                    ></i>
                  </b-button>
                  <b-button
                    variant="link"
                    class="text-danger p-0 ml-2"
                    :disabled="!status.save"
                    v-b-tooltip.hover
                    :title="$t('label.deleteX', [])"
                    :aria-label="$t('label.deleteX', [])"
                    @click="deleteConfirmation(data.item)"
                  >
                    <i class="mdi mdi-delete-outline font-size-18"></i>
                  </b-button>
                </template>
              </b-table>
              <div
                v-else-if="!status.list"
                class="d-flex justify-content-center align-items-center p-5 m-5"
              >
                <b-spinner class="m-5" variant="info" role="status" />
              </div>
              <b-alert
                v-if="!tableRows.length && status.list && !errors.list"
                show
                variant="warning"
                class="mt-0 text-center"
              >
                <i class="mdi mdi-alert-circle-outline"></i>
                {{ $t("message.noResultsFound") }}
              </b-alert>
              <div
                v-if="totalRows > 0 && !errors.list"
                class="d-flex justify-content-end mt-3"
              >
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  :per-page="perPage"
                  @change="pageNavigation"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      id="report-form"
      :title="
        $t(
          report.uuid
            ? canEditReport(report)
              ? 'label.updateX'
              : 'label.viewX'
            : 'label.createX',
          [$t('module.report')],
        )
      "
      @hidden="closeForm"
      hide-footer
      hide-header-close
      :no-close-on-backdrop="!status.save"
      :no-close-on-esc="!status.save"
      centered
      size="lg"
    >
      <b-container fluid>
        <ReportForm
          v-if="formVisible"
          :reportUUID="report.uuid"
          :read-only="!!report.uuid && !canEditReport(report)"
          :searchCriteria="searchCriteria"
          @done="closeForm"
        />
      </b-container>
    </b-modal>
    <b-modal
      id="report-delete"
      :title="$t('label.deleteX', [$t('module.report')])"
      hide-header-close
      :no-close-on-backdrop="!status.save"
      :no-close-on-esc="!status.save"
    >
      <p class="my-4">{{ $t("message.deleteConfirmation") }}</p>
      <b-alert :show="!!errors.save" variant="danger">{{
        errors.save
      }}</b-alert>
      <template #modal-footer>
        <b-button
          variant="light"
          :disabled="!status.save"
          @click="$bvModal.hide('report-delete')"
          >{{ $t("label.cancel") }}</b-button
        >
        <b-button
          variant="danger"
          :disabled="!status.save"
          @click="requestDelete"
        >
          <b-spinner v-if="!status.save" small class="mr-1" />{{
            $t("label.deleteX", [])
          }}
        </b-button>
      </template>
    </b-modal>
    <b-modal
      id="report-status"
      :title="statusAction(report)"
      hide-header-close
      :no-close-on-backdrop="!status.save"
      :no-close-on-esc="!status.save"
    >
      <p class="my-4">{{ $t("message.updateConfirmation") }}</p>
      <b-form-group :label="$t('label.status')">
        <b-form-select
          v-model="selectedStatus"
          :options="statusOptions"
          :disabled="!status.save || !canUpdateStatus"
        />
      </b-form-group>
      <b-alert :show="!!errors.save" variant="danger">{{
        errors.save
      }}</b-alert>
      <template #modal-footer>
        <b-button
          variant="light"
          :disabled="!status.save"
          @click="$bvModal.hide('report-status')"
          >{{ $t("label.cancel") }}</b-button
        >
        <b-button
          variant="info"
          :disabled="!status.save || !canUpdateStatus"
          @click="requestStatus"
        >
          <b-spinner v-if="!status.save" small class="mr-1" />{{
            $t("label.updateX", [])
          }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import i18n from "@/i18n";
import moment from "moment";
import { isReportEditable } from "@/models/api/report.model";
import { sessionHelper } from "@/utils";
import { SHARED } from "@/constants";
import { mapActions, mapState } from "vuex";
import { REPORT_MODULE } from "@/state/api/report.state";
import {
  REPORT_STATUS,
  reportTypeOptions,
  reportPlatformOptions,
  reportStatusOptions,
} from "@/constants/report";
import ReportForm from "@/views/pages/admin/report/reportForm";

export default {
  components: { ReportForm },
  data() {
    return {
      filter: "",
      type: null,
      platformType: null,
      reportStatus: null,
      currentPage: SHARED.DEFAULT_PAGE_NUMBER,
      perPage: SHARED.DEFAULT_PAGE_SIZE,
      selectedStatus: REPORT_STATUS.SUBMITTED,
      fields: [
        { key: "user", label: i18n.t("module.user"), class: "text-nowrap" },
        {
          key: "type",
          label: i18n.t("label.type"),
          sortable: true,
          class: "text-nowrap",
        },
        { key: "description", label: i18n.t("label.description") },
        {
          key: "platform_type",
          label: "Platform",
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "status",
          label: i18n.t("label.status"),
          sortable: true,
          class: "text-nowrap",
        },
        {
          key: "created_at",
          label: i18n.t("label.createdAt"),
          sortable: true,
          class: "text-nowrap",
        },
        { key: "actions", label: i18n.t("label.action"), class: "text-nowrap" },
      ],
      report: { uuid: "", status: REPORT_STATUS.SUBMITTED },
      formVisible: false,
      searchCriteria: {
        pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
        pageSize: SHARED.DEFAULT_PAGE_SIZE,
        sortBy: "created_at",
        orderBy: "desc",
      },
    };
  },
  computed: {
    ...mapState(REPORT_MODULE, ["status", "errors"]),
    canUpdateStatus() {
      const user = this.$store.state.api.auth.user?.user;
      return (
        sessionHelper.getUserPermissions(user?.permissions, user?.isSuperadmin)
          .developer === 2
      );
    },
    typeDdl() {
      return [
        { value: null, text: this.$t("label.all") },
        ...reportTypeOptions(),
      ];
    },
    platformDdl() {
      return [
        { value: null, text: this.$t("label.all") },
        ...reportPlatformOptions(),
      ];
    },
    statusDdl() {
      return [
        { value: null, text: this.$t("label.all") },
        ...reportStatusOptions(),
      ];
    },
    tableRows() {
      return this.$store.state.api.report.list.data || [];
    },
    totalRows() {
      return this.$store.state.api.report.list.pagination.total || 0;
    },
    statusOptions() {
      return reportStatusOptions();
    },
  },
  created() {
    this.getReportList(this.searchCriteria);
  },
  methods: {
    ...mapActions(REPORT_MODULE, [
      "getAllReport",
      "updateReportStatus",
      "softDeleteReport",
    ]),
    optionLabel(options, value) {
      const option = options.find((item) => item.value === value);
      return option ? option.text : "—";
    },
    formatDate(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "—";
    },
    getReportList(filter) {
      return this.getAllReport({ filter: { ...filter } });
    },
    pageNavigation(pageNumber) {
      this.searchCriteria.pageNumber = pageNumber;
      this.getReportList(this.searchCriteria);
    },
    searchListing() {
      this.currentPage = SHARED.DEFAULT_PAGE_NUMBER;
      this.searchCriteria = {
        ...this.searchCriteria,
        pageNumber: this.currentPage,
        userName: this.filter.trim(),
        type: this.type,
        platformType: this.platformType,
        status: this.reportStatus,
      };
      this.getReportList(this.searchCriteria);
    },
    sortListing({ sortBy, sortDesc }) {
      this.searchCriteria.sortBy = sortBy || "created_at";
      this.searchCriteria.orderBy = sortDesc ? "desc" : "asc";
      this.searchListing();
    },
    canEditReport(report) {
      return isReportEditable(report);
    },
    openForm(data) {
      this.report = {
        uuid: data ? data.uuid : "",
        status: data ? data.status : REPORT_STATUS.SUBMITTED,
      };
      this.formVisible = true;
      this.$nextTick(() => this.$bvModal.show("report-form"));
    },
    closeForm() {
      this.formVisible = false;
      this.report = { uuid: "", status: REPORT_STATUS.SUBMITTED };
      this.$bvModal.hide("report-form");
    },
    statusAction() {
      return this.$t("label.updateX", [this.$t("label.status")]);
    },
    statusVariant(status) {
      return status === REPORT_STATUS.RESOLVED
        ? "success"
        : status === REPORT_STATUS.REVIEWING
        ? "warning"
        : "info";
    },
    deleteConfirmation(report) {
      this.report = { uuid: report.uuid, status: report.status };
      this.$store.commit("api/report/clearSaveError");
      this.$bvModal.show("report-delete");
    },
    async requestDelete() {
      if (!this.status.save) return;
      const result = await this.softDeleteReport({
        data: { uuid: this.report.uuid },
        filter: { ...this.searchCriteria },
      });
      if (result) {
        this.$bvModal.hide("report-delete");
        const lastPage = Math.max(1, Math.ceil(this.totalRows / this.perPage));
        if (!this.errors.list && this.searchCriteria.pageNumber > lastPage) {
          this.currentPage = lastPage;
          this.pageNavigation(lastPage);
        }
      }
    },
    statusConfirmation(report) {
      if (sessionHelper.getPermissionLevel("developer") !== 2) return;
      this.selectedStatus = report.status;
      this.report = { uuid: report.uuid, status: report.status };
      this.$store.commit("api/report/clearSaveError");
      this.$bvModal.show("report-status");
    },
    async requestStatus() {
      if (
        sessionHelper.getPermissionLevel("developer") !== 2 ||
        !this.status.save ||
        !reportStatusOptions().some(
          (option) => option.value === this.selectedStatus,
        )
      )
        return;
      const result = await this.updateReportStatus({
        data: { uuid: this.report.uuid, status: this.selectedStatus },
        filter: { ...this.searchCriteria },
      });
      if (result) this.$bvModal.hide("report-status");
    },
  },
};
</script>
<style scoped>
.report-description {
  min-width: 200px;
  max-width: 400px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
